export const DESKTOP_AUTH_STATE_TYPE = "nprice:desktop-auth-state";

type DesktopReply = { requestId?: string; access?: string; warning?: string; error?: string; ok?: boolean; accounts?: unknown; nativeAuth?: boolean };

function request<T extends DesktopReply>(type: string, resultType: string, payload: Record<string, unknown> = {}, timeoutMs = 15000): Promise<T> {
  if (typeof window === "undefined" || window.parent === window) {
    return Promise.reject(new Error("데스크탑 앱에서만 사용할 수 있습니다."));
  }
  const requestId = crypto.randomUUID();
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener("message", onMessage);
      reject(new Error("데스크탑 앱 응답이 없습니다. 다시 시도해 주세요."));
    }, timeoutMs);
    function onMessage(event: MessageEvent) {
      if (event.source !== window.parent || event.data?.type !== resultType || event.data?.requestId !== requestId) return;
      window.clearTimeout(timer);
      window.removeEventListener("message", onMessage);
      const reply = event.data as T;
      if (reply.error) reject(new Error(reply.error));
      else resolve(reply);
    }
    window.addEventListener("message", onMessage);
    window.parent.postMessage({ type, requestId, ...payload }, "*");
  });
}

export async function supportsDesktopNativeAuth(): Promise<boolean> {
  try {
    const result = await request<DesktopReply>("nprice:desktop-capabilities", "nprice:desktop-capabilities-result", {}, 1200);
    return result.nativeAuth === true;
  } catch {
    return false;
  }
}

export async function requestDesktopLogin(username: string, password: string, remember: boolean) {
  const result = await request<DesktopReply>("nprice:desktop-login", "nprice:desktop-login-result", { username, password, remember });
  if (!result.access) throw new Error("로그인 토큰을 받지 못했습니다.");
  return { access: result.access, warning: result.warning || "" };
}

export async function requestDesktopSavedAccounts(): Promise<string[]> {
  const result = await request<DesktopReply>("nprice:desktop-saved-accounts", "nprice:desktop-saved-accounts-result");
  return Array.isArray(result.accounts) ? result.accounts.filter((v): v is string => typeof v === "string") : [];
}

export async function requestDesktopSavedLogin(username: string): Promise<string> {
  const result = await request<DesktopReply>("nprice:desktop-saved-login", "nprice:desktop-saved-login-result", { username });
  if (!result.access) throw new Error("로그인 토큰을 받지 못했습니다.");
  return result.access;
}

export async function requestDesktopForgetAccount(username: string): Promise<void> {
  const result = await request<DesktopReply>("nprice:desktop-forget-account", "nprice:desktop-forget-account-result", { username });
  if (!result.ok) throw new Error("저장된 계정을 삭제하지 못했습니다.");
}

export async function requestDesktopAccess(): Promise<string> {
  const result = await request<DesktopReply>("nprice:desktop-access-request", "nprice:desktop-access-result");
  return result.access || "";
}

export async function requestDesktopLogout(): Promise<void> {
  const result = await request<DesktopReply>("nprice:desktop-logout", "nprice:desktop-logout-result");
  if (!result.ok) throw new Error("데스크탑 로그아웃에 실패했습니다.");
}
